import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../LandingPage/Footer";
import useBlogs from "../../services/LandingPage/useBlogs";
import defaultAuthor from "../../images/LandingPageImage/Author_img.svg";
import Navbar from "./Navbar";

const Blogs = () => {
  const { blogs, fetchAllBlogs, loading, error } = useBlogs();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const blogsPerPage = 12;

  useEffect(() => {
    fetchAllBlogs();
  }, [fetchAllBlogs]);

  // Reset pagination whenever search changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filteredBlogs = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) return blogs || [];

    return (blogs || []).filter(
      (blog) =>
        blog.title?.toLowerCase().includes(query) ||
        blog.description?.toLowerCase().includes(query) ||
        blog.author?.toLowerCase().includes(query),
    );
  }, [blogs, searchTerm]);

  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);

  const indexOfLast = currentPage * blogsPerPage;
  const indexOfFirst = indexOfLast - blogsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <>
      <Navbar />

      <div className="min-h-screen bg-gray-50 py-10">
        <div className="max-w-6xl mx-auto px-4">
          <h1 className="text-3xl font-semibold text-center mb-8 text-gray-800">
            Get Insightful Views About WhatsApp Business
          </h1>

          {/* Search */}
          <div className="flex justify-center mb-10">
            <input
              type="text"
              placeholder="Search blogs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full md:w-1/2 px-4 py-3 border rounded-3xl focus:ring-2 focus:ring-green-500 outline-none"
            />
          </div>

          {/* Blog Grid */}
          {loading ? (
            <div className="text-center py-12">Loading blogs...</div>
          ) : error ? (
            <div className="text-center py-12 text-red-500">{error}</div>
          ) : currentBlogs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No blogs found.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {currentBlogs.map((blog) => (
                <div
                  key={blog.id}
                  className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition flex flex-col"
                >
                  {/* Image */}
                  <Link
                    to={`/blogs/${blog.post_slug}`}
                    className="block w-full h-40 overflow-hidden"
                  >
                    <img
                      src={blog.image}
                      alt={blog.imageAlt || blog.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </Link>

                  {/* Content */}
                  <div className="p-6 flex flex-col flex-grow">
                    <div>
                      <div className="flex items-center gap-2 mb-3 text-sm text-gray-500">
                        <img
                          src={blog.authorImage || defaultAuthor}
                          alt={blog.author || "Author"}
                          className="w-5 h-5 rounded-full object-cover"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = defaultAuthor;
                          }}
                        />
                        <span className="line-clamp-1">{blog.author}</span>
                      </div>

                      <h3 className="text-lg font-semibold mb-3 line-clamp-2">
                        <Link
                          to={`/blogs/${blog.post_slug}`}
                          className="hover:text-green-600 transition"
                        >
                          {blog.title}
                        </Link>
                      </h3>

                      <p className="text-gray-600 text-sm line-clamp-3">
                        {blog.description}
                      </p>
                    </div>

                    <Link
                      to={`/blogs/${blog.post_slug}`}
                      className="text-green-600 font-medium mt-auto pt-4"
                    >
                      Read more →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && !error && totalPages > 1 && (
            <div className="flex flex-wrap justify-center gap-2 mt-12">
              <button
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border rounded-lg disabled:opacity-50"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, index) => {
                const page = index + 1;

                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-4 py-2 rounded-lg ${
                      currentPage === page
                        ? "bg-green-600 text-white"
                        : "border"
                    }`}
                  >
                    {page}
                  </button>
                );
              })}

              <button
                onClick={() =>
                  setCurrentPage((page) => Math.min(totalPages, page + 1))
                }
                disabled={currentPage === totalPages}
                className="px-4 py-2 border rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Blogs;
